"use client";

import { useExperiment } from "app/_optimizely-features/useExperiment";
import type { NavLinkProps } from "./navigation-links";
import { useMemo } from "react";

// Constants for the experiment
export const CREDIT_CARD_SUBMENU_FLAG = "credit-card-submenu-flag";
export const CREDIT_CARD_SUBMENU_EXPERIMENT = "credit-card-submenu-experiment";

/**
 * This hook applies the credit card submenu A/B test to the navigation links
 *
 * @param navigationLinks The navigation links array
 * @param countryCode The user's country code
 * @returns The modified navigation links
 */
export function useCreditCardSubmenuTest(
  navigationLinks: NavLinkProps[],
  countryCode?: string | null
): NavLinkProps[] {
  // Check if the user is in the US region
  const isUS = countryCode === "US";

  // Initialize the experiment
  const experiment = useExperiment(
    CREDIT_CARD_SUBMENU_FLAG,
    CREDIT_CARD_SUBMENU_EXPERIMENT
  );

  // Memoize the result to prevent unnecessary re-renders
  return useMemo(() => {
    // Quick return if not in the US
    if (!isUS) return navigationLinks;

    if (experiment.loading) return navigationLinks;

    const modifiedNavLinks = [...navigationLinks];

    // Find the credit card item
    const creditCardIndex = modifiedNavLinks.findIndex(
      (link) => link.key === "CreditCard"
    );

    // Return unchanged if credit card not found
    if (creditCardIndex < 0) return navigationLinks;

    // Apply the experiment if it's enabled and in treatment variation
    const creditCardLink = modifiedNavLinks[creditCardIndex];
    const variationKey =
      experiment.variation ??
      (experiment.enabled ? "enabled_no_variation" : "control");

    if (experiment.variation === "treatment") {
      // Set hasMenu to true for the credit card navigation item
      modifiedNavLinks[creditCardIndex] = {
        ...creditCardLink,
        hasMenu: true,
        id: `credit-card-menu-${variationKey}-${CREDIT_CARD_SUBMENU_EXPERIMENT}`
      };
    } else {
      // For control variation, still tag the link for tracking
      modifiedNavLinks[creditCardIndex] = {
        ...creditCardLink,
        id: `credit-card-link-${variationKey}-${CREDIT_CARD_SUBMENU_EXPERIMENT}`
      };
    }

    return modifiedNavLinks;
  }, [
    navigationLinks,
    isUS,
    experiment.variation,
    experiment.enabled,
    experiment.loading
  ]);
}
